import React, { useCallback, useLayoutEffect, useRef } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';

import type { RemixContentComponentProps } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';
import { RemixContainer } from '@atlassian/malleable-ui-components';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { useRemixToAIFC } from './useRemixToAIFC.ts';

export type DynamicUiSource = {
	contentType: 'page';
	startLocalId: string;
	endLocalId: string;
	startIndex: number;
	endIndex: number;
	fragmentAdf: DocNode;
	fullAdf: DocNode;
};

/**
 * Creates a content component for RemixContentComponent using RemixContainer.
 */
export const createRemixContentComponent = () => {
	// TODO: Remove timeout when implementing: https://product-fabric.atlassian.net/browse/MAX-48
	let thinkingTimeoutId: ReturnType<typeof setTimeout> | null = null;

	const Component = ({
		handleModalClose,
		selectedContext,
		editorApi,
		onStartThinking,
		onStopThinking,
	}: RemixContentComponentProps) => {
		const { openAIFCStagingWithPrompt } = useRemixToAIFC();

		const onThinkingStateChange = useCallback(
			(isThinking: boolean) => {
				if (isThinking) {
					onStartThinking();
				} else {
					if (thinkingTimeoutId) {
						clearTimeout(thinkingTimeoutId);
						thinkingTimeoutId = null;
					}
					onStopThinking({ reopenModal: true });
				}
			},
			[onStartThinking, onStopThinking],
		);

		const constructDynamicUiSource = useCallback(async (): Promise<DynamicUiSource | undefined> => {
			const localIds = selectedContext?.localIds ?? [];
			const startLocalId = localIds[0] ?? '';
			const endLocalId = localIds[localIds.length - 1] ?? '';

			const fragmentAdf: DocNode = {
				type: 'doc',
				version: 1,
				content: (selectedContext?.selectionFragment as DocNode['content']) ?? [],
			};

			let fullAdf: DocNode = { type: 'doc', version: 1, content: [] };
			if (editorApi) {
				fullAdf = await new Promise((resolve) => {
					try {
						editorApi.core?.actions?.requestDocument?.(
							(doc: any) => {
								resolve(doc as DocNode);
							},
							{ alwaysFire: true },
						);
					} catch {
						resolve({ type: 'doc', version: 1, content: [] });
					}
				});
			}

			return {
				contentType: 'page' as const,
				startLocalId,
				endLocalId,
				// TODO: SelectionContext will eventually have these values
				startIndex: 0,
				endIndex: -1,
				fragmentAdf,
				fullAdf,
			};
		}, [editorApi, selectedContext]);

		const onOptionSelect = useCallback(
			(type: string) => {
				// TODO: Remove timeout when implementing: https://product-fabric.atlassian.net/browse/MAX-48
				thinkingTimeoutId = setTimeout(async () => {
					thinkingTimeoutId = null;
					const dynamicUiSource = await constructDynamicUiSource();
					void openAIFCStagingWithPrompt(
						selectedContext.selectionMarkdown || '',
						type,
						dynamicUiSource,
					);
				}, 1000);
			},
			[constructDynamicUiSource, openAIFCStagingWithPrompt, selectedContext.selectionMarkdown],
		);

		return (
			<RemixContainer
				selectedText={selectedContext.selectionMarkdown || ''}
				onClose={handleModalClose}
				onOptionSelect={onOptionSelect}
				onThinkingStateChange={onThinkingStateChange}
			/>
		);
	};

	const Wrapper = (props: RemixContentComponentProps) => {
		const { onStopThinking, isThinking, isModalOpen } = props;
		const { showSmartCreationModal } = useSmartCreationModalState();
		const isSmartCreationModalOpen = useRef(false);

		useLayoutEffect(() => {
			// Selected content should remain highlighted throughout the entire flow even when ephemeral modal is open
			// We deselect the content when the ephemeral modal is closed
			if (isSmartCreationModalOpen.current && !showSmartCreationModal && isThinking) {
				if (thinkingTimeoutId) {
					clearTimeout(thinkingTimeoutId);
					thinkingTimeoutId = null;
				}
				onStopThinking();
			}
			isSmartCreationModalOpen.current = showSmartCreationModal;
		}, [showSmartCreationModal, isThinking, onStopThinking]);

		return (isModalOpen || isThinking) && !showSmartCreationModal ? (
			<ConfluenceConversationAssistant>
				<Component {...props} />
			</ConfluenceConversationAssistant>
		) : null;
	};

	return Wrapper;
};
